import * as CookieConsentModule from 'vanilla-cookieconsent';
import type {
  CookieConsentConfig,
  CookieValue,
  UserPreferences,
} from 'vanilla-cookieconsent';
import type { ClientConfigurationResponse } from './types';
import type { ScriptSourcePattern } from './sbjs-blocker';
import { updateGtagConsent } from './consent-mode';
import { actionFromAcceptType, sendConsentLog } from './logging';

const CACHE_KEY_PREFIX = 'yargi-cmp-config:';

export type CookieConsentApi = {
  run(config: CookieConsentConfig): Promise<void>;
  acceptCategory(categories: string | string[], excludedCategories?: string[]): void;
  acceptedCategory(categoryName: string): boolean;
  getCookie(): CookieValue;
  getUserPreferences(): UserPreferences;
  show(force?: boolean): void;
  hide(): void;
};

const DEFAULT_CATEGORIES = {
  necessary: { enabled: true, readOnly: true },
};

const DEFAULT_LANGUAGE = {
  default: 'tr',
  translations: {
    tr: {
      consentModal: {
        title: 'Çerezleri kullanıyoruz',
        description: 'Size en iyi deneyimi sunmak için çerezleri kullanıyoruz.',
        acceptAllBtn: 'Tümünü Kabul Et',
        acceptNecessaryBtn: 'Tümünü Reddet',
        showPreferencesBtn: 'Tercihleri Yönet',
        closeIconLabel: 'Kapat',
      },
      preferencesModal: {
        title: 'Çerez Tercihleri',
        acceptAllBtn: 'Tümünü Kabul Et',
        acceptNecessaryBtn: 'Tümünü Reddet',
        savePreferencesBtn: 'Seçimi Kaydet',
        closeIconLabel: 'Kapat',
        sections: [
          {
            title: 'Zorunlu Çerezler',
            description: 'Sitenin çalışması için gereklidir.',
            linkedCategory: 'necessary',
          },
        ],
      },
    },
  },
};

function resolveCookieConsentApi(mod: object): CookieConsentApi {
  const record = mod as {
    run?: CookieConsentApi['run'];
    acceptCategory?: CookieConsentApi['acceptCategory'];
    acceptedCategory?: CookieConsentApi['acceptedCategory'];
    getCookie?: CookieConsentApi['getCookie'];
    getUserPreferences?: CookieConsentApi['getUserPreferences'];
    show?: CookieConsentApi['show'];
    hide?: CookieConsentApi['hide'];
    default?: CookieConsentApi;
  };

  if (
    typeof record.run === 'function' &&
    typeof record.acceptedCategory === 'function' &&
    typeof record.getCookie === 'function' &&
    typeof record.getUserPreferences === 'function'
  ) {
    const runFn = record.run;
    const acceptedCatFn = record.acceptedCategory;
    const getCookieFn = record.getCookie;
    const getUserPrefFn = record.getUserPreferences;
    const acceptCatFn = record.acceptCategory;
    const showFn = record.show;
    const hideFn = record.hide;

    return {
      run: (config) => runFn.call(record, config),
      acceptCategory: (cats, excl) => (acceptCatFn ? acceptCatFn.call(record, cats, excl) : undefined),
      acceptedCategory: (cat) => acceptedCatFn.call(record, cat),
      getCookie: () => getCookieFn.call(record),
      getUserPreferences: () => getUserPrefFn.call(record),
      show: (force) => (showFn ? showFn.call(record, force) : undefined),
      hide: () => (hideFn ? hideFn.call(record) : undefined),
    };
  }

  const fromDefault = record.default;
  if (
    fromDefault &&
    typeof fromDefault.run === 'function' &&
    typeof fromDefault.acceptedCategory === 'function' &&
    typeof fromDefault.getCookie === 'function' &&
    typeof fromDefault.getUserPreferences === 'function'
  ) {
    const runFn = fromDefault.run;
    const acceptedCatFn = fromDefault.acceptedCategory;
    const getCookieFn = fromDefault.getCookie;
    const getUserPrefFn = fromDefault.getUserPreferences;
    const acceptCatFn = fromDefault.acceptCategory;
    const showFn = fromDefault.show;
    const hideFn = fromDefault.hide;

    return {
      run: (config) => runFn.call(fromDefault, config),
      acceptCategory: (cats, excl) => (acceptCatFn ? acceptCatFn.call(fromDefault, cats, excl) : undefined),
      acceptedCategory: (cat) => acceptedCatFn.call(fromDefault, cat),
      getCookie: () => getCookieFn.call(fromDefault),
      getUserPreferences: () => getUserPrefFn.call(fromDefault),
      show: (force) => (showFn ? showFn.call(fromDefault, force) : undefined),
      hide: () => (hideFn ? hideFn.call(fromDefault) : undefined),
    };
  }

  throw new Error('[Yargı CMP] vanilla-cookieconsent API surface not found');
}

const defaultCookieConsentApi = resolveCookieConsentApi(CookieConsentModule);
let cookieConsentApi: CookieConsentApi = defaultCookieConsentApi;

// Exposed per docs/cookieconsent-3.1.0/types/index.d.ts Window.CookieConsent convention;
// also required for scanner in-page consent simulation (apps/scanner).
if (typeof window !== 'undefined') {
  (window as unknown as { CookieConsent: CookieConsentApi }).CookieConsent = defaultCookieConsentApi;
}

export function __setCookieConsentApiForTests(next: CookieConsentApi | null): void {
  cookieConsentApi = next ?? defaultCookieConsentApi;
  if (typeof window !== 'undefined') {
    (window as unknown as { CookieConsent: CookieConsentApi }).CookieConsent = cookieConsentApi;
  }
}
/**
 * Read the site-specific script patterns out of the cached configuration.
 *
 * Synchronous on purpose: the blocker needs them before the first script on
 * the page runs, and localStorage is the only source available that early.
 * Returning [] on any problem is intentional — a returning visitor with a
 * corrupt cache must still get a working CMP.
 */
export function readCachedScriptPatterns(siteId: string): ScriptSourcePattern[] {
  try {
    const cached = localStorage.getItem(CACHE_KEY_PREFIX + siteId);
    if (!cached) return [];

    const parsed = JSON.parse(cached) as ClientConfigurationResponse;

    return normalizeScriptPatterns(parsed?.firstPartyTagPaths);
  } catch {
    return [];
  }
}

function normalizeScriptPatterns(value: unknown): ScriptSourcePattern[] {
  if (!Array.isArray(value)) return [];

  const out: ScriptSourcePattern[] = [];

  for (const entry of value) {
    if (!entry || typeof entry !== 'object') continue;

    const record = entry as { pattern?: unknown; category?: unknown };
    const pattern = typeof record.pattern === 'string' ? record.pattern.trim() : '';
    const category = record.category;

    if (!pattern) continue;
    if (
      category !== 'analytics' &&
      category !== 'advertisement' &&
      category !== 'functionality'
    ) {
      continue;
    }

    out.push({ pattern, category });
  }

  return out;
}

export type LoadCmpOptions = {
  /**
   * Fired whenever category acceptance may have changed:
   * after run() (returning visitors via onConsent), onFirstConsent, onChange.
   * Used by the tracking cookie blocker (src/sbjs-blocker.ts).
   */
  onTrackingConsentChange?: (state: {
    analytics: boolean;
    advertisement: boolean;
    functionality: boolean;
  }) => void;
  /**
   * Site-specific script sources from the fetched configuration.
   * Fired as soon as the config lands, before CookieConsent.run().
   */
  onScriptPatterns?: (patterns: ScriptSourcePattern[]) => void;
};

export async function loadCmp(
  siteId: string,
  apiBaseUrl: string,
  options: LoadCmpOptions = {},
): Promise<void> {
  const cacheKey = CACHE_KEY_PREFIX + siteId;
  let config: ClientConfigurationResponse | null = null;

  try {
    const response = await fetch(
      `${apiBaseUrl}/api/client/sites/${encodeURIComponent(siteId)}/configuration`,
      {
        headers: { Accept: 'application/json' },
      },
    );
    if (response.ok) {
      config = (await response.json()) as ClientConfigurationResponse;
      try {
        localStorage.setItem(cacheKey, JSON.stringify(config));
      } catch {
        // ignore
      }
    }
  } catch {
    // ignore
  }

  if (!config) {
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        config = JSON.parse(cached) as ClientConfigurationResponse;
      }
    } catch {
      // ignore
    }
  }

  if (!config) {
    // Fail-safe: no CookieConsent.run — sbjs blocker (if enabled by entry) stays on.
    return;
  }

  // Before run(): a late pattern is still better than none for scripts the
  // page inserts after the banner appears.
  const scriptPatterns = normalizeScriptPatterns(config.firstPartyTagPaths);
  if (scriptPatterns.length > 0) {
    options.onScriptPatterns?.(scriptPatterns);
  }

  const configurationVersion = config.configurationVersion;

  const fireGtagUpdate = () =>
    updateGtagConsent((category) => cookieConsentApi.acceptedCategory(category));

  const syncTrackingGate = () => {
    options.onTrackingConsentChange?.({
      analytics: cookieConsentApi.acceptedCategory('analytics'),
      advertisement: cookieConsentApi.acceptedCategory('advertisement'),
      functionality: cookieConsentApi.acceptedCategory('functionality'),
    });
  };

  const logInitial = () => {
    sendConsentLog('initial', apiBaseUrl, siteId, configurationVersion, cookieConsentApi);
  };

  const logChange = () => {
    const { acceptType } = cookieConsentApi.getUserPreferences();
    sendConsentLog(
      actionFromAcceptType(acceptType),
      apiBaseUrl,
      siteId,
      configurationVersion,
      cookieConsentApi,
    );
  };

  const rawCc = (config.cookieconsent as unknown as CookieConsentConfig) || {};
  const ccConfig: CookieConsentConfig = {
    ...rawCc,
    autoShow: rawCc.autoShow ?? true,
    categories: rawCc.categories ?? DEFAULT_CATEGORIES,
    language: rawCc.language ?? DEFAULT_LANGUAGE,
    onFirstConsent: () => {
      fireGtagUpdate();
      logInitial();
      syncTrackingGate();
    },
    // onConsent also runs on every page load with valid consent — use it to
    // unlock tracking for returning visitors without re-logging (logging stays off).
    onConsent: () => {
      fireGtagUpdate();
      syncTrackingGate();
    },
    onChange: () => {
      fireGtagUpdate();
      logChange();
      syncTrackingGate();
    },
  };

  await waitForDomReady();
  await cookieConsentApi.run(ccConfig);
}

/**
 * Hold run() until the document has a body.
 *
 * client.js is synchronous in <head> by design, so the blocker is active from
 * the first tick. That leaves run() racing the parser: it fires as soon as the
 * configuration lands, and when that is fast — a warm cache, a slow <head> —
 * the parser has not reached <body> yet. The library resolves its `root`
 * option ("body") with querySelector, gets null, and throws
 * "Cannot read properties of null (reading 'appendChild')" part-way through
 * setup. _ccRun ends up true with no banner in the DOM, so the visitor has no
 * way to consent at all and every category stays denied forever.
 *
 * Intermittent by nature, which is what made it look like the CMP "sometimes"
 * worked.
 */
function waitForDomReady(): Promise<void> {
  if (typeof document === 'undefined' || document.readyState !== 'loading') {
    return Promise.resolve();
  }

  return new Promise<void>((resolve) => {
    document.addEventListener('DOMContentLoaded', () => resolve(), { once: true });
  });
}
